import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SocialLoginButton from './components/SocialLoginButton';
import CollectibleTypeSelector from './components/CollectibleTypeSelector';
import MembershipTierModal from './components/MembershipTierModal';
import RegistrationForm from './components/RegistrationForm';
import ValuePreview from './components/ValuePreview';

const Registration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTier, setSelectedTier] = useState('free');
  const [registrationData, setRegistrationData] = useState(null);

  const steps = [
    { id: 1, name: 'Account', icon: 'User' },
    { id: 2, name: 'Interests', icon: 'Heart' },
    { id: 3, name: 'Complete', icon: 'Check' }
  ];

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/collection-dashboard');
    }
  }, [navigate]);

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    try {
      // Mock social login
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock successful login
      localStorage.setItem('authToken', 'mock-jwt-token');
      localStorage.setItem('userProfile', JSON.stringify({
        id: 'user-123',
        name: 'John Collector',
        email: 'john@example.com',
        provider: provider,
        membershipTier: 'free',
        collectibleTypes: ['watches', 'art'],
        joinedAt: new Date()?.toISOString()
      }));
      
      navigate('/collection-dashboard');
    } catch (error) {
      console.error('Social login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationSubmit = async (formData) => {
    setIsLoading(true);
    try {
      // Registration validation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setRegistrationData(formData);
      setCurrentStep(2);
    } catch (error) {
      console.error('Registration failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeToggle = (typeId) => {
    setSelectedTypes(prev => 
      prev?.includes(typeId) 
        ? prev?.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleCompleteRegistration = async () => {
    setIsLoading(true);
    try {
      // Mock final registration
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store user data
      localStorage.setItem('authToken', 'mock-jwt-token');
      localStorage.setItem('userProfile', JSON.stringify({
        id: 'user-123',
        name: `${registrationData?.firstName} ${registrationData?.lastName}`,
        email: registrationData?.email,
        membershipTier: selectedTier,
        collectibleTypes: selectedTypes,
        subscribeNewsletter: registrationData?.subscribeNewsletter,
        joinedAt: new Date()?.toISOString()
      }));
      
      setCurrentStep(3);
      
      // Redirect after showing success
      setTimeout(() => {
        navigate('/collection-dashboard');
      }, 3000);
    } catch (error) {
      console.error('Registration completion failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMembershipSelect = (tierId) => {
    setSelectedTier(tierId);
    setShowMembershipModal(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            {/* Social Login Options */}
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Join Crazy Rare Club
                </h2>
                <p className="text-muted-foreground">
                  Start tracking your valuable collectibles today
                </p>
              </div>

              <div className="space-y-3">
                <SocialLoginButton 
                  provider="google" 
                  onClick={() => handleSocialLogin('google')}
                  disabled={isLoading}
                />
                <SocialLoginButton 
                  provider="apple" 
                  onClick={() => handleSocialLogin('apple')}
                  disabled={isLoading}
                />
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-background text-muted-foreground">
                    Or create account with email
                  </span>
                </div>
              </div>
            </div>

            {/* Registration Form */}
            <RegistrationForm 
              onSubmit={handleRegistrationSubmit}
              isLoading={isLoading}
            />

            {/* Login Link */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <a 
                  href="/login" 
                  className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
                >
                  Sign in here
                </a>
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <CollectibleTypeSelector 
              selectedTypes={selectedTypes}
              onTypeToggle={handleTypeToggle}
            />
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => setShowMembershipModal(true)}
                iconName="Crown"
                iconPosition="left"
                className="flex-1"
              >
                Choose Membership Plan
              </Button>
              
              <Button
                variant="default"
                onClick={handleCompleteRegistration}
                loading={isLoading}
                disabled={isLoading || selectedTypes?.length === 0}
                iconName="ArrowRight"
                iconPosition="right"
                className="flex-1"
              >
                Continue with {selectedTier === 'free' ? 'Free' : 'Premium'} Plan
              </Button>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Selected: {selectedTypes?.length} collectible type{selectedTypes?.length !== 1 ? 's' : ''}
                {selectedTypes?.length === 0 && ' (Please select at least one)'}
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-success to-green-600 rounded-full flex items-center justify-center animate-scale-in">
              <Icon name="Check" size={40} color="white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Welcome to the Club!
              </h2>
              <p className="text-muted-foreground">
                Your account has been created successfully. Redirecting to your dashboard...
              </p>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Icon name="Sparkles" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">
                  Your personalized experience is ready!
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                We've set up your dashboard with {selectedTypes?.length} collectible categories
                and your {selectedTier} membership benefits.
              </p>
            </div>
            <div className="animate-pulse">
              <div className="w-8 h-8 mx-auto bg-primary rounded-full"></div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/homepage" className="flex items-center space-x-3 group">
              <img 
                src="/assets/images/crc_logo_dia_logo_500-1759554868300.png" 
                alt="Crazy Rare Club Symbol" 
                className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-105"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
              <div className="hidden sm:block">
                <img 
                  src="/assets/images/crc_logo_dia_text_500-1759554658424.png" 
                  alt="Crazy Rare Club" 
                  className="h-8 object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </a>

            <div className="flex items-center space-x-4">
              <a 
                href="/homepage"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </header>
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Form */}
            <div className="order-2 lg:order-1">
              <div className="max-w-md mx-auto">
                {/* Progress Steps */}
                {currentStep < 3 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between">
                      {steps?.map((step, index) => (
                        <div key={step?.id} className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                            currentStep >= step?.id
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-border text-muted-foreground'
                          }`}>
                            <Icon name={step?.icon} size={16} />
                          </div>
                          
                          <div className="ml-3">
                            <p className={`text-sm font-medium ${
                              currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                              {step?.name}
                            </p>
                          </div>

                          {index < steps?.length - 1 && (
                            <div className={`w-12 h-0.5 mx-4 transition-colors duration-300 ${
                              currentStep > step?.id ? 'bg-primary' : 'bg-border'
                            }`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step Content */}
                <div className="animate-fade-in">
                  {renderStepContent()}
                </div>
              </div>
            </div>

            {/* Right Column - Value Preview */}
            <div className="order-1 lg:order-2">
              <div className="sticky top-8">
                <ValuePreview selectedTypes={selectedTypes} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Membership Tier Modal */}
      <MembershipTierModal
        isOpen={showMembershipModal}
        onClose={() => setShowMembershipModal(false)}
        onSelectTier={handleMembershipSelect}
      />
      {/* Footer */}
      <footer className="border-t border-border bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-6 text-xs text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={14} className="text-success" />
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Lock" size={14} className="text-success" />
                <span>Privacy Protected</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={14} className="text-success" />
                <span>Industry Trusted</span>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground">
              © {new Date()?.getFullYear()} Crazy Rare Club. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Registration;